import App from 'next/app'
import { Head, NextNProgress, Page } from '@/components'
import api, { createApi } from '@/lib/api'
import '@/assets/styles/fonts.css'
import { HostProvider } from '@/contexts/HostContext'
import { ScheduleProvider } from '@/contexts/ScheduleContext'
import getConfig from 'next/config'
import * as Sentry from '@sentry/node'
import Error from './_error.js'
import Router from 'next/router'
import * as gtag from '@/lib/gtag'
import * as ym from '@/lib/ym'
import CookiesBanner from '@/components/CookiesBanner/CookieBanner'
import '@/lib/i18n';
import { useEffect } from 'react';

const isProdMode = process.env.NODE_ENV === 'production'

if (isProdMode) {
  Router.events.on('routeChangeComplete', (url) => {
    gtag.pageview(url)
    //ym.pageview(url)
  })
}

const {
  publicRuntimeConfig: { host, sentryDsn },
} = getConfig()

Sentry.init({
  dsn: `${sentryDsn}`,
  enabled: process.env.NODE_ENV === 'production',
})

export default class MyApp extends App {
  // store errorStatus here if page with error was rendered on server
  static lastStatusCode = null

  static async getInitialProps({ Component, ctx }) {
    let pageProps = {}

    const currentApi = ctx.req ? createApi() : api

    let errorStatus = ctx.err ? ctx.err.statusCode : ctx.res ? ctx.res.statusCode : null
    let hasError = false;

    if (ctx.err) {
      Sentry.captureException(ctx.err)
    }

    try {
      if (Component.getInitialProps) {
        pageProps = await Component.getInitialProps({
          ...ctx,
          api: currentApi,
          statusCode: errorStatus || MyApp.lastStatusCode,
        })
      }
    } catch (error) {
      errorStatus = error.statusCode || 500
      hasError = true
      ctx.res && ctx.res.status(errorStatus)
      console.error(error)
      Sentry.captureException(error)
    }

    const [menu, schedule] = await Promise.all([currentApi.menu(), currentApi.schedule()])

    const currentHost =
      process.env.NODE_ENV === 'development'
        ? host
        : ctx.req
          ? `${ctx.req.hostname}`
          : location.host

    return {
      pageProps,
      hasError: Component === Error || hasError,
      host: currentHost,
      errorStatus,
      menu,
      schedule,
      apiCacheData: currentApi.extractCache(),
    }
  }

  constructor(props) {
    super(props)
    if (typeof window !== 'undefined') {
      api.fillCache(this.props.apiCacheData)
    }
  }



  componentDidMount() {
    if (this.props.errorStatus && +this.props.errorStatus !== 200) {
      // save errorStatus if page with error was rendered on server

      MyApp.lastStatusCode = this.props.errorStatus;
    }

    const cookies = Object.fromEntries(
        document.cookie.split('; ').map(c => c.split('='))
    );
    const params = new URLSearchParams(window.location.search);

    this.checkCookie(cookies, params,'gclid')
    this.checkCookie(cookies, params,'gbraid')
    this.checkCookie(cookies, params,'wbraid')
  }

  checkCookie(cookies, params,name) {
    const value = params.get(name);
    if (cookies[name] === value) return ;
    if (value) {
      document.cookie = name + '=' + value + ';path=/;max-age=' + (90*24*3600);
    }
  }

  render() {
    const { Component, pageProps, menu, schedule, hasError, host } = this.props;
    const decodedStr = decodeURIComponent(this.props.router.asPath)
    const hasCyrillic = /[А-Яа-яЁё]/.test(decodedStr);

    const errorStatus = hasCyrillic ? 404 : this.props.errorStatus || MyApp.lastStatusCode
    const scheduleData = schedule?.data ?? [];

  
    if (hasError) {
      return errorStatus === 404 ? (
        <>
          <Head />
          <ScheduleProvider data={scheduleData}>
            <Page menu={menu}>
              <NextNProgress />
              <Error {...pageProps} statusCode={errorStatus} />
            </Page>
          </ScheduleProvider>
        </>
      ) : (
        <>
          <Head />
          <NextNProgress />
          <Error {...pageProps} statusCode={errorStatus} />
        </>
      )
    }

    return (
      <>
        <Head />
        <HostProvider host={host}>
          <ScheduleProvider data={scheduleData}>
            <Page menu={menu} {...pageProps}>
              <NextNProgress />
              <Component {...pageProps} />
              <CookiesBanner />
            </Page>
          </ScheduleProvider>
        </HostProvider>
      </>
    )
  }
}
